import * as HoverCard from "@radix-ui/react-hover-card";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { newChatRoomPath } from "@/react/helpers/urlHelpers";
import type { AgentProfileResponse } from "@/react/hooks/agent_profile/useAgentProfileInfo";
import { useProfileDrawer } from "@circle-react/components/Drawers/ProfileDrawer";
import { AvatarWithProgress } from "@circle-react/components/Leaderboard/Dashboard/MyProfile/AvatarWithProgress";
import { useCommunityInboxStore } from "@circle-react/hooks/chatsV2/store";
import { Icon } from "@circle-react-shared/Icon";
import { Button } from "@circle-react-shared/uikit/Button";
import { Typography } from "@circle-react-shared/uikit/Typography";

interface CardContentProps {
  agentProfileInfo: AgentProfileResponse | null;
  avatarURL?: string;
  closeCard: () => void;
  name?: string;
  publicUid?: string;
}

export const CardContent = ({
  agentProfileInfo,
  publicUid,
  avatarURL,
  name = "",
  closeCard,
}: CardContentProps) => {
  const drawer = useProfileDrawer();

  const { setCurrentBotProfile } = useCommunityInboxStore();
  const history = useHistory();

  const onNewConversationClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    setCurrentBotProfile(agentProfileInfo?.profile ?? null);
    history.push({
      pathname: newChatRoomPath(),
    });
  };

  return (
    <HoverCard.Content
      className="border-primary bg-primary z-[1059] w-full max-w-[320px] rounded-xl border p-6 shadow-xl"
      sideOffset={5}
      side="right"
    >
      <div className="flex items-start justify-between">
        <div className="text-dark mr-4 min-w-0 flex-1">
          <Typography.LabelMd weight="semibold">
            <span className="text-dark">{name}</span>
          </Typography.LabelMd>
          <br />

          <div className="text-light break-words">
            <Typography.LabelSm weight="regular">
              <span className="text-light">{agentProfileInfo?.bio}</span>
            </Typography.LabelSm>
          </div>
        </div>
        <div className="shrink-0">
          <AvatarWithProgress avatarUrl={avatarURL} name={name} size="small" />
        </div>
      </div>
      <div className="mt-4 space-y-2" />
      <div className="mt-4 flex w-full space-x-2">
        {publicUid && (
          <Button
            className="!border-primary flex items-center justify-center space-x-2 !px-3 !py-1.5 !font-medium"
            full
            variant="secondary"
            onClick={() => {
              void drawer.show({
                memberPublicId: publicUid,
              });
              closeCard();
            }}
          >
            <Icon
              type="16-user"
              useWithFillCurrentColor
              size={16}
              aria-hidden
            />
            <span>{t("view_profile")}</span>
          </Button>
        )}
        {agentProfileInfo?.can_interact && (
          <Button onClick={onNewConversationClick} variant="circle" full>
            <Icon
              type="plus"
              useWithFillCurrentColor
              size={16}
              aria-hidden
              className="mr-1"
            />
            <span>{t("community_inbox.new_conversation")}</span>
          </Button>
        )}
      </div>
    </HoverCard.Content>
  );
};
